import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackPromoReveal, trackPromoCopy } from '../../../utils/promoStats';
import { openPartnerLink } from '../../../utils/openPartnerLink';
import { getPromoActionLabel } from '../../../utils/promoUi';

interface ExclusiveStore {
  storeId: string;
  label: string;
  order: number;
}

interface ExclusiveSettings {
  isEnabled: boolean;
  title: string;
  subtitle: string;
  stores: ExclusiveStore[];
}

interface StoreData {
  id: string;
  name: string;
  logo: string;
  description: string;
  category: string;
  isActive: boolean;
  promos: {
    id: string;
    title: string;
    code: string;
    discount: string;
    description: string;
    expiryDate: string;
    isExclusive: boolean;
    isActive: boolean;
    partnerUrl: string;
  }[];
}

export default function ExclusiveSection() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<ExclusiveSettings | null>(null);
  const [storesMap, setStoresMap] = useState<Record<string, StoreData>>({});
  const [revealedCodes, setRevealedCodes] = useState<Set<string>>(new Set());

  useEffect(() => {
    const raw = localStorage.getItem('exclusive_settings');
    if (!raw) return;
    const parsed: ExclusiveSettings = JSON.parse(raw);
    if (!parsed.isEnabled || parsed.stores.length === 0) return;
    setSettings(parsed);

    const storesRaw = localStorage.getItem('admin_stores_v2');
    if (storesRaw) {
      const allStores: StoreData[] = JSON.parse(storesRaw);
      const map: Record<string, StoreData> = {};
      allStores.forEach(s => { map[s.id] = s; });
      setStoresMap(map);
    }
  }, []);

  if (!settings || !settings.isEnabled || settings.stores.length === 0) return null;

  const validStores = settings.stores
    .map(es => ({ ...es, store: storesMap[es.storeId] }))
    .filter(es => es.store && es.store.isActive);

  if (validStores.length === 0) return null;

  const getStoreSlug = (name: string) =>
    name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9а-яё\-]/gi, '').replace(/\.ru$/, '').replace(/\.com$/, '');

  const getBestPromo = (store: StoreData) => {
    const active = (store.promos || []).filter(p => p.isActive);
    const exclusive = active.find(p => p.isExclusive);
    return exclusive || active[0] || null;
  };

  const handleReveal = (promoId: string, code: string, storeName: string, discount: string, title: string, partnerUrl: string) => {
    setRevealedCodes(prev => new Set([...prev, promoId]));
    if (code) {
      navigator.clipboard.writeText(code);
    }
    trackPromoReveal({ promoId, promoCode: code, promoTitle: title, storeName, storeId: promoId, discount });
    trackPromoCopy({ promoId, promoCode: code, promoTitle: title, storeName, storeId: promoId, discount });
    openPartnerLink(partnerUrl);
  };

  return (
    <section className="py-16 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-10">
          <div className="inline-flex items-center space-x-2 bg-amber-100 text-amber-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
            <i className="ri-vip-crown-line"></i>
            <span>Эксклюзив</span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">{settings.title}</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">{settings.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {validStores.map(({ store, label }) => {
            const promo = getBestPromo(store);
            return (
              <div
                key={store.id}
                className="relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-amber-200 group"
              >
                {/* Exclusive ribbon */}
                <div className="absolute top-3 right-3 z-10">
                  <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm">
                    {label}
                  </span>
                </div>

                <div className="p-6">
                  <div
                    className="flex items-center space-x-4 mb-4 cursor-pointer"
                    onClick={() => navigate(`/store/${getStoreSlug(store.name)}`)}
                  >
                    <img
                      src={store.logo}
                      alt={store.name}
                      className="w-11 h-11 rounded-xl object-cover border border-gray-100 group-hover:scale-105 transition-transform"
                    />
                    <div>
                      <h3 className="font-bold text-gray-900 group-hover:text-amber-700 transition-colors">{store.name}</h3>
                      <p className="text-xs text-gray-500">{store.category}</p>
                    </div>
                  </div>

                  {promo ? (
                    <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-900">{promo.title || promo.description}</span>
                        <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                          {promo.discount}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 mt-3">
                        <div className="flex-1 bg-white border border-amber-200 rounded-lg px-3 py-2">
                          <span className={`font-mono font-bold text-sm text-gray-900 ${
                            !revealedCodes.has(promo.id) ? 'filter blur-sm select-none' : ''
                          }`}>
                            {promo.code}
                          </span>
                        </div>
                        <button
                          onClick={() => handleReveal(promo.id, promo.code, store.name, promo.discount, promo.title || promo.description, promo.partnerUrl)}
                          className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all cursor-pointer whitespace-nowrap text-sm font-semibold"
                        >
                          {revealedCodes.has(promo.id) ? (
                            <><i className="ri-check-line mr-1"></i>Скопировано</>
                          ) : (
                            getPromoActionLabel(promo.code)
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => navigate(`/store/${getStoreSlug(store.name)}`)}
                      className="w-full bg-amber-50 text-amber-700 py-3 rounded-xl text-sm font-semibold hover:bg-amber-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Смотреть все акции
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
